import { Capacitor } from '@capacitor/core';
import { toInAppPath } from './cms-banner-navigation';

const PDF_PATH_PATTERN = /\.pdf$/i;

export interface NativeInternalLinkDeps {
  navigate: (url: string) => Promise<boolean>;
}

type DismissablePopover = HTMLElement & { dismiss?: () => Promise<boolean> };

function isPdfHref(href: string): boolean {
  try {
    return PDF_PATH_PATTERN.test(new URL(href, document.baseURI).pathname);
  } catch {
    return false;
  }
}

/** Absolute URL whose host is the running app (Capacitor origin included). */
function pathFromSameOrigin(href: string): string | null {
  try {
    const parsed = new URL(href);
    if (parsed.host !== globalThis.location.host) {
      return null;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function inAppPathForHref(href: string): string | null {
  return toInAppPath(href) ?? pathFromSameOrigin(href);
}

function anchorFromEvent(event: Event): HTMLAnchorElement | null {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (node instanceof HTMLAnchorElement) {
      return node;
    }
  }
  const target = event.target;
  const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
  const closest = element?.closest('a[href]');
  return closest instanceof HTMLAnchorElement ? closest : null;
}

/**
 * Native-only: keep La Banca routes inside the Angular router.
 * Covers target=_blank and absolute site URLs, including anchors inside WittyBot's shadow root.
 * Relative routerLinks and PDFs are left to their existing handlers.
 */
export function startNativeInternalLinkInterceptor(deps: NativeInternalLinkDeps): () => void {
  if (!Capacitor.isNativePlatform()) {
    return () => undefined;
  }

  const onClick = (event: Event) => {
    const anchor = anchorFromEvent(event);
    const href = anchor?.getAttribute('href')?.trim();
    if (!anchor || !href || href === '#' || isPdfHref(href)) {
      return;
    }

    const inAppPath = inAppPathForHref(href);
    if (!inAppPath) {
      return;
    }

    const opensOutside = /^https?:\/\//i.test(href) || (anchor.getAttribute('target') ?? '').toLowerCase() === '_blank';
    if (!opensOutside) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    dismissMailboxPopover(event);
    void deps.navigate(inAppPath);
  };

  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}

function dismissMailboxPopover(event: Event): void {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (!(node instanceof HTMLElement) || node.tagName !== 'ION-POPOVER') {
      continue;
    }
    const popover = node as DismissablePopover;
    if (typeof popover.dismiss === 'function') {
      void popover.dismiss();
    }
  }
}
